import { skipToken, useQuery, useQueryClient } from "@tanstack/react-query";

import { TenantRead, getRanaApiV1 } from "@nens/rana/api-v1";
import {
  Schematisation,
  SchematisationsListRequest,
  getHCCApi,
} from "@nens/threedi/openapi-client";

import { createHccUpdateForQueries } from "./hooks/create-updater-for-queries";
import { useTenantQuery } from "./query-tenant";
import { combineQueries } from "./utils/combine-queries";
import { convertThreediOrg } from "./utils/convert-threedi-org";
import { fetchAllPagesHcc } from "./utils/fetch-all-pages";

export interface SchematisationWithId extends Schematisation {
  id: number;
}

const getHccSchematisationQuery = (schematisationId: number | undefined) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-schematisation", schematisationId],
    queryFn: schematisationId
      ? async (): Promise<SchematisationWithId> => {
          const result = await getHCCApi().schematisationsRead({ id: schematisationId });

          if ("id" in result && typeof result.id === "number") {
            return { ...result, id: result.id };
          }

          // This should never happen, but the HCC API types are incorrect. Sigh
          throw new Error("Schematisation does not contain an id");
        }
      : skipToken,
    enabled: !!schematisationId,
  });
};

export const useHccSchematisationQuery = (id: number | undefined) =>
  useQuery(getHccSchematisationQuery(id));

// an exception for the catalog, since it is needed for the useQuerySearchState
export const useGetHccSchematisationQuery = () => {
  return getHccSchematisationQuery;
};

export type RanaSchematisationsListRequest = Omit<
  SchematisationsListRequest,
  "ownerUniqueIdIn" | "ranaProjectIdIsnull"
> & { fetchAll?: boolean };

type HccSchematisationsList = {
  count: number;
  results: Schematisation[];
};

const getHccSchematisationsListQuery = (
  tenant: TenantRead | undefined,
  { fetchAll = false, ...props }: RanaSchematisationsListRequest
) =>
  getRanaApiV1().getQueryOptions<HccSchematisationsList>({
    queryKey: ["hcc-schematisations", "list", tenant?.id, fetchAll, props],
    queryFn: tenant
      ? () => {
          const args: SchematisationsListRequest = {
            ...props,
            // Always filter on the organisations of the tenant, because we only want to show schematisations that are relevant for the current tenant.
            // Only retrieve schematisations which do not belong to a project
            ownerUniqueIdIn: tenant.threediOrganisations.map(convertThreediOrg),
            // These filters ensure we only get schematisations that should be displayed in the catalogue:
            // - ranaProjectIdIsnull: Only include schematisations that are not associated with a project
            // - archived: Exclude archived schematisations
            // - lastUpdatedIsnull: Only include schematisations that have been updated (not orphaned)
            ranaProjectIdIsnull: true,
            archived: false,
            lastUpdatedIsnull: false,
          };

          if (!fetchAll) {
            return getHCCApi().schematisationsList(args);
          }

          return fetchAllPagesHcc((pageArgs) =>
            getHCCApi().schematisationsList({ ...args, ...pageArgs })
          );
        }
      : skipToken,
  });

// Used by both the catalogue schematisation list and the project-file feature, so it stays shared here.
export const useHccSchematisationsListQuery = (props: RanaSchematisationsListRequest) => {
  const tenantQuery = useTenantQuery();
  const schematisationsQuery = useQuery(getHccSchematisationsListQuery(tenantQuery.data, props));
  return combineQueries(schematisationsQuery, tenantQuery);
};

export const useAllHccSchematisationsListQuery = () => {
  const tenantQuery = useTenantQuery();
  const schematisationsQuery = useQuery(
    getHccSchematisationsListQuery(tenantQuery.data, { fetchAll: true })
  );
  return combineQueries(schematisationsQuery, tenantQuery);
};

const getTenantHasSchematisationsQuery = (tenant: TenantRead | undefined) => {
  return getRanaApiV1().getQueryOptions({
    queryKey: ["hcc-schematisations", "tenant-has-schematisations", tenant?.id],
    queryFn: tenant
      ? async () => {
          const response = await getHCCApi().schematisationsList({
            ownerUniqueIdIn: tenant.threediOrganisations.map(convertThreediOrg),
            ranaProjectIdIsnull: true,
            archived: false,
            lastUpdatedIsnull: false,
            limit: 1,
          });
          return response.count > 0;
        }
      : skipToken,
  });
};

export const useTenantHasSchematisationsQuery = () => {
  const tenantQuery = useTenantQuery();
  const hasSchematisationsQuery = useQuery(getTenantHasSchematisationsQuery(tenantQuery.data));
  return combineQueries(hasSchematisationsQuery, tenantQuery);
};

// INVALIDATION

// Response already contains the full updated schematisation, so update the single cache and the
// matching entry in every cached list directly.
export const useUpdateHccSchematisationQuery = (id: number | undefined) => {
  const tenantQuery = useTenantQuery();
  const queryClient = useQueryClient();

  const updateQueries = createHccUpdateForQueries<Schematisation>(queryClient, {
    getSingleQueryKey: (schematisationId) => getHccSchematisationQuery(schematisationId).queryKey,
    getListQueryKey: () =>
      getHccSchematisationsListQuery(tenantQuery.data, {}).queryKey.slice(0, -2), //Slice of all the props in the query key, so it matches all list queries for this tenant
  });

  return (update: Schematisation) => {
    if (id === undefined)
      return console.warn("Cannot update HCC schematisation queries: id is undefined");
    return updateQueries(id, update);
  };
};
